import React from 'react';

import { CurrencyFormat } from '@ha/intl';
import { useIntl } from 'ha/i18n';
import { SmartTrack as Track } from 'ha/components/track/SmartTrack';

import { Grid, Link, Typography, units } from '@hbf/dsl';
import { UtilityBills } from 'ha/models/Listing/types';
import { UtilityBillsLabelByValue } from 'ha/models/ListingUtilityBills/translations';
import { makeStyles } from 'tss-react/mui';
import { DesiredDates } from 'ha/models/BookingDates/types';
import { ListingPricingValues } from '@ha/api/v2/types/Listing';
import dayjs from 'dayjs';

interface OwnProps {
  currency: string;
  billsType: UtilityBills;
  onBillsClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  pricingValues: ListingPricingValues;
  desiredDates: DesiredDates;
}

const useStyles = makeStyles()(theme => ({
  price: {
    [theme.breakpoints.down('lg')]: {
      fontSize: units.rem(1.5),
    },
  },
}));
const getAllMonthsBetweenTwoDates = (dates: DesiredDates) => {
  let startDate = dayjs(dates.startDate!).set('date', 1);
  const endDate = dayjs(dates.endDate!).set('date', 2);

  const result = [];
  while (startDate.isBefore(endDate)) {
    result.push(startDate.format('MM'));
    startDate = startDate.add(1, 'month');
  }
  return result;
};

export const PriceRange: React.FC<OwnProps> = ({
  currency,
  pricingValues,
  desiredDates,
  billsType,
  onBillsClick,
}) => {
  const { T, formatCurrency, formatCurrencyRange } = useIntl();
  const { classes } = useStyles();
  const subtitle = `${T('Monthly')} (${T(
    UtilityBillsLabelByValue[billsType],
  )})`;

  const selectedMonths = getAllMonthsBetweenTwoDates(desiredDates);
  const selectedMonthPrices = selectedMonths.map(
    (month: string) => pricingValues.monthly?.[`m-${month}`],
  );

  const minPrice = Math.min(...selectedMonthPrices);
  const maxPrice = Math.max(...selectedMonthPrices);
  const currencyFormat =
    currency === 'EUR'
      ? CurrencyFormat.WITH_CURRENCY_SYMBOL_NO_FRACTION_NO_GROUPING
      : CurrencyFormat.WITH_CURRENCY_CODE_NO_FRACTION_NO_GROUPING;
  const priceRange =
    minPrice !== maxPrice
      ? formatCurrencyRange(currency, minPrice, maxPrice, currencyFormat)
      : formatCurrency(minPrice, currency, currencyFormat);

  return (
    <Grid container direction="column" data-test-locator="Listing/Price">
      <Grid item>
        <Grid container>
          <Grid item>
            <Typography.H2
              className={classes.price}
              component="p"
              data-test-locator="Listing/Price/Price"
              color="mint"
            >
              {priceRange}
            </Typography.H2>
          </Grid>
        </Grid>
      </Grid>
      <Grid item>
        {onBillsClick ? (
          <Track name="Clicked Bills Anchor link" type="link">
            <Link
              to=""
              onClick={onBillsClick}
              data-test-locator="Listing/Price/Subtitle/Link"
            >
              <Typography.Caption
                data-test-locator="Listing/Price/Subtitle"
                color="muted"
              >
                {subtitle}
              </Typography.Caption>
            </Link>
          </Track>
        ) : (
          <Typography.Caption
            data-test-locator="Listing/Price/Subtitle"
            color="muted"
          >
            {subtitle}
          </Typography.Caption>
        )}
      </Grid>
    </Grid>
  );
};
